import { useContext, useEffect, useState } from "react";
import { Alert, Button, Descriptions, Spin, Tag, Timeline, Tooltip, Typography, message } from "antd";
import { CheckCircleOutlined, CopyOutlined, WarningOutlined } from "@ant-design/icons";
import getVoipCall, { VoipCallDetail } from "../../../domain/use_cases/leedflow/voipCalls/getVoipCall";
import getVoipCallRecording from "../../../domain/use_cases/leedflow/voipCalls/getVoipCallRecording";
import AuthenticationContext from "../../contexts/auth/AuthenticationContext";
import { MISSED_REASON_COLORS, MISSED_REASON_LABELS } from "./missedReasonLabels";

interface VoipCallTimelineProps {
    roomName: string;
    /** Firestore document id (`row.id`) — needed for the recording fetch,
     *  which resolves by DOCUMENT id, not by room name (LEED-1135). */
    docId: string;
}

// Le `room_sid` est l'id de session côté LiveKit Cloud : le dashboard s'ouvre
// toujours sur la même URL, seul le projet (prod) est fixe.
const LIVEKIT_SESSION_URL = 'https://cloud.livekit.io/projects/p_19icz5wdlqw/sessions';

const fmtTime = (ts: number): string => new Date(ts).toLocaleTimeString('fr-FR', { hour12: false });

const relOffset = (ts: number, base: number): string => {
    const d = ts - base;
    if (d === 0) return '+0s';
    return `+${(d / 1000).toFixed(2)}s`;
};

// Les explications de labels vivaient ici en copie du builder backend, et
// avaient dérivé. Elles arrivent maintenant avec chaque évènement
// (`event.explanation`), depuis `VOIP_TIMELINE_EVENTS` — source unique.

const VoipCallTimeline: React.FC<VoipCallTimelineProps> = ({ roomName, docId }) => {
    const authContext = useContext(AuthenticationContext);
    const [detail, setDetail] = useState<VoipCallDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
    const [recordingLoading, setRecordingLoading] = useState(false);
    const [recordingError, setRecordingError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const res = await getVoipCall(authContext.authentication, roomName);
            if (!cancelled) {
                setDetail(res);
                setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [authContext.authentication, roomName]);

    // Revoke the blob URL whenever it changes or the component unmounts, to avoid leaking memory.
    useEffect(() => {
        return () => {
            if (recordingUrl) URL.revokeObjectURL(recordingUrl);
        };
    }, [recordingUrl]);

    const loadRecording = async () => {
        setRecordingLoading(true);
        setRecordingError(false);
        try {
            const url = await getVoipCallRecording(authContext.authentication, docId);
            if (url) setRecordingUrl(url);
            else {
                // `null` = session ou identifiant manquant, sans appel backend.
                setRecordingError(true);
                message.error("Impossible de charger l'enregistrement.");
            }
        } catch (e) {
            // Message du backend : « aucun enregistrement » n'est pas une panne.
            setRecordingError(true);
            message.error(e instanceof Error ? e.message : "Impossible de charger l'enregistrement.");
        } finally {
            setRecordingLoading(false);
        }
    };

    if (loading) return <Spin size="small" style={{ margin: '1rem' }} />;
    if (!detail) return <Alert type="error" message="Impossible de charger le détail de l'appel." showIcon />;

    const base = detail.timeline.length > 0 ? detail.timeline[0].ts : 0;

    const copyJson = () => {
        navigator.clipboard.writeText(JSON.stringify(detail, null, 2));
        message.success('JSON copié dans le presse-papier');
    };

    return (
        <div style={{ padding: '0.5rem 1rem 1rem 1rem', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
                <Button icon={<CopyOutlined />} size="small" onClick={copyJson}>Copier JSON</Button>
            </div>
            <div style={{ minWidth: 320, flex: 1 }}>
                <Typography.Text strong>Timeline</Typography.Text>{' '}
                <Tag>{detail.scenarioLabel}</Tag>
                {detail.timeline.length === 0 ? (
                    <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
                        Aucun évènement horodaté sur ce document.
                    </Typography.Paragraph>
                ) : (
                    <Timeline
                        style={{ marginTop: 12 }}
                        items={detail.timeline.map((event) => {
                            // Une échéance n'est pas un fait : elle est rendue en
                            // pointillés gris pour qu'on ne la lise pas comme un
                            // évènement qui a eu lieu (LEED-1538).
                            const isDeadline = event.kind === 'deadline';
                            return {
                                color: isDeadline ? 'gray' : 'blue',
                                children: (
                                    <span>
                                        <Typography.Text code>{fmtTime(event.ts)}</Typography.Text>{' '}
                                        <Typography.Text type="secondary">{relOffset(event.ts, base)}</Typography.Text>{' '}
                                        <Tooltip title={event.explanation}>
                                            <Typography.Text
                                                underline
                                                type={isDeadline ? 'secondary' : undefined}
                                                italic={isDeadline}
                                                style={{ cursor: 'help' }}
                                            >
                                                {event.label}
                                            </Typography.Text>
                                        </Tooltip>
                                        {isDeadline ? <Typography.Text type="secondary"> (échéance)</Typography.Text> : null}
                                        {event.extra ? <Typography.Text type="secondary"> ({event.extra})</Typography.Text> : null}
                                    </span>
                                ),
                            };
                        })}
                    />
                )}
            </div>

            <div style={{ minWidth: 360, flex: 1 }}>
                {detail.anomalies.length > 0 ? (
                    <Alert
                        type={detail.anomalies.some((a) => a.severity === 'error') ? 'error' : 'warning'}
                        showIcon
                        icon={<WarningOutlined />}
                        message={`Écarts au modèle « ${detail.scenarioLabel} »`}
                        description={
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {detail.anomalies.map((anomaly) => (
                                    <li key={`${anomaly.code}-${anomaly.detail}`}>
                                        <Typography.Text code>{anomaly.code}</Typography.Text> {anomaly.detail}
                                    </li>
                                ))}
                            </ul>
                        }
                        style={{ marginBottom: 16 }}
                    />
                ) : null}

                {detail.failures.length === 0 ? (
                    <Alert
                        type="success"
                        showIcon
                        icon={<CheckCircleOutlined />}
                        message="Aucune erreur stampée sur l'appel."
                        style={{ marginBottom: 16 }}
                    />
                ) : (
                    <Alert
                        type="warning"
                        showIcon
                        icon={<WarningOutlined />}
                        message="Échecs / sorties non nominales"
                        description={
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {detail.failures.map((failure) => (
                                    <li key={failure}>{failure}</li>
                                ))}
                            </ul>
                        }
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Descriptions size="small" column={1} bordered>
                    <Descriptions.Item label="room_sid">
                        {detail.roomSid ? (
                            <Typography.Link
                                href={`${LIVEKIT_SESSION_URL}/${detail.roomSid}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {detail.roomSid}
                            </Typography.Link>
                        ) : (
                            '—'
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label="sip_call_id">{detail.sipCallId ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="resolution_status">{detail.resolutionStatus ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="recording_phase">{detail.recordingPhase ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="last_left_kind">{detail.lastLeftKind ?? '—'}</Descriptions.Item>
                    {detail.missed ? (
                        <Descriptions.Item label="cause du manqué">
                            {detail.missedReason ? (
                                <Tag color={MISSED_REASON_COLORS[detail.missedReason]}>
                                    {MISSED_REASON_LABELS[detail.missedReason]}
                                </Tag>
                            ) : (
                                '—'
                            )}
                        </Descriptions.Item>
                    ) : null}
                    <Descriptions.Item label="transcription">
                        {detail.hasTranscription ? <Tag color="green">présente</Tag> : <Tag>absente</Tag>}
                    </Descriptions.Item>
                    <Descriptions.Item label="owner">{detail.ownerEmail ?? detail.ownerUid ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="enregistrement">
                        {detail.recordingStoragePath ||
                        (detail.urlRecording && detail.urlRecording.startsWith('http')) ? (
                            // C'est `recordingStoragePath` qui décide, pas `url_recording` : la
                            // racine porte une sentinelle (`pending`, `not_available`) dans des
                            // cas où le fichier existe et où le même endpoint sait le streamer.
                            // Deux familles mesurées en production le 2026-09-18 : le triage
                            // `call-summary` (LEED-1568, `recorded === false`) et surtout les
                            // appels basculés en messagerie, dont le contenu part dans
                            // `Voicemails` pendant que la coquille `Calls` garde le `pending`
                            // posé à sa création — 1019 appels sur 14 jours, tous jouables.
                            // Élargir ici ne re-surface pas les cas masqués exprès : le backend
                            // renvoie déjà `recordingStoragePath: null` sur un contact
                            // confidentiel (LEED-1426). L'URL http reste en second pour les docs
                            // GSM sans chemin Storage.
                            <span>
                                {recordingUrl ? (
                                    <audio controls autoPlay src={recordingUrl} style={{ height: 32, verticalAlign: 'middle' }} />
                                ) : (
                                    <Button size="small" loading={recordingLoading} onClick={loadRecording}>
                                        {recordingError ? 'Réessayer' : 'Écouter'}
                                    </Button>
                                )}
                                {detail.recorded === false ? (
                                    <>
                                        {' '}
                                        <Tag color="orange">masqué (recorded: false)</Tag>
                                        {detail.recordingHiddenReason ? (
                                            <Typography.Text type="secondary"> {detail.recordingHiddenReason}</Typography.Text>
                                        ) : null}
                                    </>
                                ) : null}
                            </span>
                        ) : (
                            detail.urlRecording ?? '—'
                        )}
                    </Descriptions.Item>
                </Descriptions>
            </div>
        </div>
    );
};

export default VoipCallTimeline;
